import React from 'react';
import _ from 'lodash';

import {
  ApprovalTypeName,
  QueryReturnAuthorizationsFilterInput,
  ReturnAuthorizationsActivity_DeprecatedQueryVariables,
  useReturnAuthorizationsActivity_DeprecatedLazyQuery
} from 'generated/graphql';
import {
  errorUtil,
  listQueryUtil
} from 'iron-forge-react-modules';

import Big from 'big.js';
import { DateTime } from 'luxon';
import * as types from './types';



const useListQuery = listQueryUtil.genUseListQuery<types.IColumnId>();



export interface ILineItem {
  id: string;
  sku: string;
  name: string;
  quantity: number;
  refundAmountCents: Big;
}

export interface IReturnAuthorization {
  id: string;
  storeId: string;
  orderId: string;
  customerEmail: string;
  returnNumber: string;
  returnDate: DateTime;
  returnLabelUrl: string;
  returnTrackingNumber: string;
  approvalStatus: ApprovalTypeName;
  shipmentStatus: string;
  isReceived: boolean;
  acceptanceStatus: ApprovalTypeName;
  refundStatus: ApprovalTypeName;

  lineItems: ILineItem[];
}

export interface IData {
  returnAuthorizations: IReturnAuthorization[];
}

export function useQuery() {
  const useWrappedLazyQuery = __genWrappedLazyQuery();

  return useListQuery({
    useLazyQuery: useWrappedLazyQuery,
    genFilter2GqlFilter: () => __filter2GqlFilter,
    clientColumn2GqlSortField: {
      returnNumber: 'returnNumber',
      returnDate: 'returnDate'
    },
    initQuerySort: {
      columnId: 'returnDate',
      direction: 'descending'
    },
    defaultFilterArgs: {},
    gqlResultData2GqlPageInfo: gqlData => gqlData?.returnAuthorizations.pageInfo,
    gqlResultData2TupleData: (gqlTuple): IData => {
      const returnAuthorizations = _.map(
        gqlTuple.data?.returnAuthorizations.nodes ?? [],
        (node): IReturnAuthorization => ({
          id: node.id,
          storeId: node.store.id,
          orderId: node.order.id,
          customerEmail: node.customer.email,
          returnDate: DateTime.fromISO(node.returnDate),
          returnNumber: node.returnNumber,

          shipmentStatus: node.shipment?.deliveryScanEvent?.scannedAt
            ? "Delivered"
            : node.shipment?.departureScanEvent?.scannedAt ? "In Transit" : "Unknown",

          returnLabelUrl: node.shipment?.labelUrl ?? '',
          returnTrackingNumber: node.shipment?.trackingNumber ?? '',
          approvalStatus: node.approval?.approvalType.name ?? 'pending',
          isReceived: !_.isNil(node.itemReceipt),
          acceptanceStatus: node.acceptance?.approvalType.name ?? 'pending',
          refundStatus: node.refund?.approvalType.name ?? 'pending',
          lineItems: _.map(node.lineItems, (lineItem): ILineItem => ({
            id: lineItem.id,
            name: lineItem.product.name,
            sku: lineItem.product.sku,
            quantity: lineItem.quantity,
            refundAmountCents: new Big(lineItem.refundAmountCents)
          }))
        }));

      return {
        returnAuthorizations
      };
    }
  });
}



function __genWrappedLazyQuery() {
  function useWrappedQuery() {
    const [fnQuery, tuple] = errorUtil.useWrapLazyQueryOrMutationTuple(
      useReturnAuthorizationsActivity_DeprecatedLazyQuery({
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache'
      })
    );

    const fnWrappedQuery = React.useCallback(
      (params: {
        variables: Pick<ReturnAuthorizationsActivity_DeprecatedQueryVariables, 'page' | 'filter'>;
      }) => {
        fnQuery({
          variables: {
            page: params.variables.page,
            filter: {
              ...params.variables.filter
            },
            sort: {
              direction: 'descending',
              field: 'returnDate'
            }
          }
        });
      },
      [fnQuery]
    );

    return [
      fnWrappedQuery,
      tuple
    ] as const;
  }

  return useWrappedQuery;
}



function __filter2GqlFilter(filter?: types.IFilter): QueryReturnAuthorizationsFilterInput {
  return {
    omniFreeText: '',
    after: filter?.formState.after?.toISOString(),
    before: filter?.formState.before?.toISOString(),
    storeId: filter?.formState.store?.id,
    authorizationApprovalStatus: filter?.formState.authorizationApprovalStatus,
    acceptanceApprovalStatus: filter?.formState.acceptanceApprovalStatus,
    refundApprovalStatus: filter?.formState.refundApprovalStatus,
    isReceived: filter?.formState.isReceived
  };
}
